import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { useBasePath } from '@/lib/paths'
import { formatCurrency, formatDate } from '@/lib/utils'
import { FormationSummary } from '@/types'
import { Building2, Plus } from 'lucide-react'

type Props = {
  formations: FormationSummary[]
  search: string
  pagination: PaginationMeta
}

export default function FormationsIndex({ formations, search, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout
      title="Formations"
      breadcrumbs={[{ label: 'Formations' }]}
    >
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Formations</h1>
          <p className="text-sm text-muted-foreground">Manage your business registrations</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm">
              <Link href={`${basePath}/formations/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New Formation
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <SearchInput value={search} placeholder="Search formations..." />
        </div>

        {formations.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Building2 className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No formations yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Start your first business registration</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/formations/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Start Your First Business
              </Link>
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg">
            {formations.map((formation, i) => (
              <Link
                key={formation.id}
                href={`${basePath}/formations/${formation.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${
                  i > 0 ? 'border-t' : ''
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{formation.legal_name}</span>
                      <StageBadge stage={formation.stage} variant="formation" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formation.entity?.name} · {formation.entity?.jurisdiction_code}
                      {formation.assumed_name && ` · DBA: ${formation.assumed_name}`}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <div className="text-sm font-medium text-foreground">
                    {formation.entity ? formatCurrency(formation.entity.state_fee_cents + formation.entity.service_fee_cents) : '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDate(formation.created_at)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
